import {
  Card,
  Checkbox,
  Combobox,
  FAIcon,
  Field,
  FieldLabel,
  ModuleWrapper,
  StickyFilters,
  Toggles,
  UserCombobox,
} from '@/components'
import type { ToggleOption } from '@/components'
import { cn } from '@/lib/utils'
import { KEEP_SCROLL_STATE } from '@/utils/hooks/use-scroll-restoration'
import { useTranslate } from '@ur-solutions/react-hooks'
import { ChartColumnStacked } from 'lucide-react'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { isBrowser, isMobile } from 'react-device-detect'
import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router'
import { InfoLabel } from '../driver-grade/grade-parts'
import {
  CURRENT_YEAR,
  DriverWorkFilterContext,
  useDriverWorkFilter,
  type useDriverWorkFilterForm,
} from './driver-work-filter'
import { DriverWorkExport, type DriverWorkTab } from './driver-work-export'
import { STICKY_TABLE_TOP_VAR } from './sticky-table'

const YEARS_BACK = 4
// StickyFilters pins below the fixed mobile header on touch devices.
const MOBILE_HEADER_HEIGHT = 78

export const DRIVER_WORK_CHARTS_PATH = '/analytics/driver-work-hours'
export const DRIVER_WORK_TABLES_PATH = '/analytics/driver-work-hours-table'
export const DRIVER_WORK_WEEKS_PATH = '/analytics/driver-work-hours-weeks'
export const DRIVER_OVERTIME_PATH = '/analytics/driver-overtime'

type View = 'charts' | 'tables' | 'weeks'

const VIEW_PATHS: Record<View, string> = {
  charts: DRIVER_WORK_CHARTS_PATH,
  tables: DRIVER_WORK_TABLES_PATH,
  weeks: DRIVER_WORK_WEEKS_PATH,
}

const TABS: { id: DriverWorkTab; to: string; paths: string[] }[] = [
  {
    id: 'hours',
    to: DRIVER_WORK_CHARTS_PATH,
    paths: Object.values(VIEW_PATHS),
  },
  {
    id: 'overtime',
    to: DRIVER_OVERTIME_PATH,
    paths: [DRIVER_OVERTIME_PATH],
  },
]

/** Which driver and week the chart view has drilled into, kept in the URL. */
export const useDrillDown = () => {
  const [params, setParams] = useSearchParams()

  const update = useCallback(
    (changes: Record<string, string | null>, { keepScroll = false } = {}) => {
      setParams(
        previous => {
          const next = new URLSearchParams(previous)
          for (const [key, value] of Object.entries(changes))
            if (value) next.set(key, value)
            else next.delete(key)
          return next
        },
        keepScroll ? { state: KEEP_SCROLL_STATE } : undefined
      )
    },
    [setParams]
  )

  return { user: params.get('user'), week: params.get('week'), update }
}

const useActiveTab = (): DriverWorkTab => {
  const { pathname } = useLocation()
  const active = TABS.find(tab =>
    tab.paths.some(path => pathname.endsWith(path))
  )
  return active?.id ?? 'hours'
}

const Tabs: React.FC = () => {
  const activeTab = useActiveTab()
  const labels = useTranslate({
    hours: 'driver-work.tab-hours',
    overtime: 'driver-work.tab-overtime',
  })

  return (
    <nav className="mb-4 flex gap-6 border-b border-stone-200">
      {TABS.map(tab => {
        const active = tab.id === activeTab
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={cn(
              '-mb-px border-b-2 border-transparent pb-2 text-sm font-semibold text-stone-500 no-underline hover:text-stone-900 hover:no-underline',
              active && 'border-stone-900 text-stone-900'
            )}
          >
            {labels[tab.id]}
          </NavLink>
        )
      })}
    </nav>
  )
}

const ViewToggles: React.FC = () => {
  const translations = useTranslate({
    charts: 'driver-work.view-charts',
    tables: 'driver-work.view-tables',
    weeks: 'driver-work.view-weeks',
  })
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const current = (Object.keys(VIEW_PATHS) as View[]).find(view =>
    pathname.endsWith(VIEW_PATHS[view])
  )

  const options: ToggleOption<View>[] = [
    {
      value: 'charts',
      label: (
        <span title={translations.charts}>
          <ChartColumnStacked size={18} />
        </span>
      ),
    },
    {
      value: 'tables',
      label: (
        <span title={translations.tables}>
          <FAIcon icon="list" />
        </span>
      ),
    },
    {
      value: 'weeks',
      label: (
        <span title={translations.weeks}>
          <FAIcon icon="table" />
        </span>
      ),
    },
  ]

  return (
    <Toggles
      value={current ?? 'charts'}
      options={options}
      variant="white"
      size={isMobile ? 'md' : 'lg'}
      onChange={view => void navigate(VIEW_PATHS[view])}
    />
  )
}

/** Height of an element, kept up to date as it resizes. */
const useHeight = <TElement extends HTMLElement>() => {
  const ref = useRef<TElement>(null)
  const [height, setHeight] = useState(0)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    const observer = new ResizeObserver(() =>
      setHeight(element.getBoundingClientRect().height)
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return { ref, height }
}

const DriverWorkFilters: React.FC<{
  driverFilter: boolean
  cardRef: React.Ref<HTMLDivElement>
  onYearChange?(): void
}> = ({ driverFilter, cardRef, onYearChange }) => {
  const translations = useTranslate({
    year: 'common.year',
    drivers: 'common.drivers',
    allDrivers: 'driver-work.all-drivers',
    vehicles: 'common.vehicles',
    ownedOnly: 'driver-work.owned-only',
    ownedOnlyInfo: 'driver-work.owned-only-info',
  })
  const { filter, updateFilter } = useDriverWorkFilter()
  const years = Array.from({ length: YEARS_BACK + 1 }, (_, index) => {
    const value = CURRENT_YEAR - index
    return { value, label: String(value) }
  })

  return (
    <StickyFilters className="mb-4">
      {/* Measured for the sticky table headers. StickyFilters squares the top
          of its direct child once stuck and the card follows along; neither
          gets a shadow while stuck. */}
      <div
        ref={cardRef}
        className={cn(
          'rounded-xl *:transition-[border-radius,box-shadow]',
          'in-data-[stuck]:*:rounded-t-none in-data-[stuck]:*:shadow-none',
          'in-data-[stuck]:shadow-none!'
        )}
      >
        <Card className="p-0" contentClassName="p-0">
          <div className="grid gap-4 p-4 md:flex md:flex-wrap md:justify-start">
            <Field className="md:w-[200px]">
              <FieldLabel>{translations.year}</FieldLabel>
              <Combobox
                mode="single"
                noSearch
                items={years}
                value={filter.year}
                onChange={year => {
                  updateFilter({ year })
                  onYearChange?.()
                }}
              />
            </Field>
            {driverFilter && (
              <Field className="md:w-[350px]">
                <FieldLabel>{translations.drivers}</FieldLabel>
                <UserCombobox
                  mode="multiple"
                  value={filter.drivers}
                  placeholder={translations.allDrivers}
                  onChange={drivers => updateFilter({ drivers })}
                />
              </Field>
            )}
            <Field className="md:w-auto">
              <FieldLabel htmlFor="driver-work-owned-only">
                {translations.vehicles}
              </FieldLabel>
              {/* Styled like the combobox triggers beside it. */}
              <label
                htmlFor="driver-work-owned-only"
                className="flex h-15.5 cursor-pointer items-center gap-3 rounded-xl border border-gray-200 bg-quaternary px-6 text-base font-normal"
              >
                <Checkbox
                  id="driver-work-owned-only"
                  size="md"
                  className="bg-white"
                  value={filter.ownedVehiclesOnly}
                  onChange={checked =>
                    updateFilter({ ownedVehiclesOnly: checked === true })
                  }
                />
                <InfoLabel
                  plain
                  label={translations.ownedOnly}
                  info={translations.ownedOnlyInfo}
                />
              </label>
            </Field>
          </div>
        </Card>
      </div>
    </StickyFilters>
  )
}

export const DriverWorkLayout: React.FC<{
  form: ReturnType<typeof useDriverWorkFilterForm>
  /** Called after the year changes, e.g. to leave a week of the old year. */
  onYearChange?(): void
  driverName?: string
  /** Show the Charts / Tables / Week overview switch of the working hours tab. */
  views?: boolean
  driverFilter?: boolean
  headerActions?: React.ReactNode
  children: React.ReactNode
}> = ({
  form,
  onYearChange,
  driverName,
  views = true,
  driverFilter = true,
  headerActions,
  children,
}) => {
  const translations = useTranslate({ title: 'driver-work.title' })
  const tab = useActiveTab()
  const filters = useHeight<HTMLDivElement>()
  const tableTop = (isBrowser ? 0 : MOBILE_HEADER_HEIGHT) + filters.height

  return (
    <DriverWorkFilterContext.Provider value={form}>
      <ModuleWrapper
        title={
          <>
            {translations.title}
            {driverName && (
              <span className="font-normal text-stone-500">
                {' '}
                · {driverName}
              </span>
            )}
          </>
        }
        headerActions={headerActions}
        headerRight={
          <div className="flex items-center gap-3">
            {views && <ViewToggles />}
            <DriverWorkExport tab={tab} />
          </div>
        }
      >
        <Tabs />
        <DriverWorkFilters
          driverFilter={driverFilter}
          cardRef={filters.ref}
          onYearChange={onYearChange}
        />
        <div
          style={
            { [STICKY_TABLE_TOP_VAR]: `${tableTop}px` } as React.CSSProperties
          }
        >
          {children}
        </div>
      </ModuleWrapper>
    </DriverWorkFilterContext.Provider>
  )
}
